import { get, post, put } from './request'
import type { AuthUserInfo, LoginRequest, LoginResponse } from '@/types/auth'

interface BackendAuthUser {
  id?: string | number
  userId?: string | number
  username?: string
  email?: string
  nickname?: string
  avatar?: string
  roles?: string[]
  passwordResetRequired?: boolean
}

interface BackendLoginResult {
  token?: string
  accessToken?: string
  jwt?: string
  tokenType?: string
  expiresIn?: number
  user?: BackendAuthUser
  userInfo?: BackendAuthUser
  profile?: BackendAuthUser
  userId?: string | number
  id?: string | number
}

/**
 * 将后端用户对象转换为前端统一用户信息。
 *
 * @param user 后端返回的用户信息
 * @param fallbackUsername 兜底用户名
 * @param fallbackUserId 兜底用户 ID
 * @returns 前端统一用户信息
 */
function normalizeAuthUser(
  user: BackendAuthUser | null | undefined,
  fallbackUsername: string,
  fallbackUserId?: string | number,
): AuthUserInfo {
  const resolvedUserId = user?.id ?? user?.userId ?? fallbackUserId
  if (resolvedUserId === undefined || resolvedUserId === null || resolvedUserId === '') {
    throw new Error('登录返回缺少用户ID')
  }

  return {
    id: String(resolvedUserId),
    username: user?.username || fallbackUsername,
    email: user?.email || '',
    nickname: user?.nickname,
    avatar: user?.avatar,
    roles: Array.isArray(user?.roles) ? user.roles : [],
    passwordResetRequired: user?.passwordResetRequired === true,
  }
}

/**
 * 规范化登录接口返回结构。
 *
 * @param result 后端原始登录响应
 * @param fallbackUsername 兜底用户名
 * @returns 前端统一登录结果
 */
function normalizeLoginResponse(result: BackendLoginResult, fallbackUsername: string): LoginResponse {
  const token = result.token || result.accessToken || result.jwt || ''
  if (!token) {
    throw new Error('登录返回缺少令牌')
  }

  const user = result.user || result.userInfo || result.profile
  return {
    token: String(token),
    tokenType: result.tokenType,
    expiresIn: result.expiresIn,
    user: normalizeAuthUser(user, fallbackUsername, result.userId ?? result.id),
  }
}

/**
 * 调用登录接口并返回统一结构。
 *
 * @param payload 登录请求参数
 * @returns 登录结果
 */
export function login(payload: LoginRequest): Promise<LoginResponse> {
  return post<BackendLoginResult>('/api/v1/auth/login', payload).then(result =>
    normalizeLoginResponse(result, payload.username),
  )
}

/**
 * 获取当前登录用户信息。
 *
 * @returns 当前登录用户
 */
export function getCurrentUser(): Promise<AuthUserInfo> {
  return get<BackendAuthUser>('/api/v1/auth/me').then(user => normalizeAuthUser(user, user?.username || ''))
}

/**
 * 更新个人资料（昵称/邮箱/手机号）。
 *
 * @param data 要更新的字段
 * @returns 更新后的用户信息
 */
export function updateProfile(data: {
  nickname?: string
  email?: string
  phone?: string
}): Promise<AuthUserInfo> {
  return put<BackendAuthUser>('/api/v1/auth/profile', data).then(user =>
    normalizeAuthUser(user, user?.username || ''),
  )
}

/**
 * 修改密码。
 *
 * @param oldPassword 旧密码
 * @param newPassword 新密码
 */
export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  return put('/api/v1/auth/password', { oldPassword, newPassword })
}

/**
 * 个人通知配置。
 */
export interface NotificationConfig {
  dingtalkEnabled: boolean
  dingtalkWebhook?: string
  dingtalkSecret?: string
  emailEnabled: boolean
  emailRecipients?: string
  smsEnabled: boolean
  smsRecipients?: string
}

/**
 * 获取当前用户的个人通知配置。
 *
 * @returns 通知配置
 */
export function getNotificationConfig(): Promise<NotificationConfig> {
  return get<NotificationConfig>('/api/v1/auth/notification-config')
}

/**
 * 保存当前用户的个人通知配置。
 *
 * @param config 通知配置
 */
export function saveNotificationConfig(config: NotificationConfig): Promise<void> {
  return put('/api/v1/auth/notification-config', config)
}
